import { SxProps } from "@mui/material";
import { REQUEST_FORM_FIELDS } from "core/consts";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { TransitionalCareRequest } from "core/types";
import { fromUnixTime, getUnixTime } from "date-fns";
import {
  DatePickerInputField,
  DateSelectorProps,
} from "ds_legacy/components/DatePickerInputField";
import RSButton from "ds_legacy/components/RSButton";
import {
  SelectInput,
  SelectInputProps,
} from "ds_legacy/components/SelectInput";
import { useOntologyOptions } from "ds_legacy/components/SelectInput/Ontology";
import {
  TextInputField,
  TextInputFieldProps,
} from "ds_legacy/components/TextInputField";
import {
  TimePickerPresenter,
  TimePresenterProps,
} from "ds_legacy/components/TimePicker/input";
import { WHITE } from "ds_legacy/materials/colors";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding } from "ds_legacy/materials/metrics";
import { Body, FONT_WEIGHT_SEMI_BOLD } from "ds_legacy/materials/typography";
import {
  LARGE_INPUT_MIN_WIDTH,
  SMALL_INPUT_MIN_WIDTH,
  SectionRow,
  WithLabel,
} from "dsl/ecosystems/PatientForms/components";
import { Trash2Icon } from "lucide-react";
import { useRef } from "react";
import { isValid } from "react-forms-state";
import { handleInputFocusOnSubmit } from "react-forms-state/src/Form";
import { useTranslations } from "translations";
import { getSectionIdLabel } from ".";
import { FormField, useRequestListContext } from "../RequestListContext";
import { ACTION_TYPES } from "../RequestListContext/reducer";
import { CardIconButton } from "./RequestCard";

const LARGE_INPUT_WIDTH = "40%";
const SMALL_INPUT_WIDTH = `20%`;

const DisconnectedTextInput = ({
  currentState,
  elementName,
  label,
  large,
  ...props
}: TextInputFieldProps & {
  currentState: TransitionalCareRequest;
  elementName: Exclude<FormField, typeof REQUEST_FORM_FIELDS.REQUEST_SENT_DATE>;
  large?: boolean;
}) => {
  const { dispatch, state } = useRequestListContext();

  const validation = state.validation[elementName];

  return (
    <WithLabel
      bold
      error={!isValid(validation)}
      label={label}
      required
      wrapperSx={{
        minWidth: large ? LARGE_INPUT_MIN_WIDTH : SMALL_INPUT_MIN_WIDTH,
        width: large ? LARGE_INPUT_WIDTH : SMALL_INPUT_WIDTH,
      }}
    >
      <TextInputField
        label={null}
        marginOverride="0"
        required
        style={{
          background: WHITE,
        }}
        testId={elementName}
        validation={validation}
        value={currentState[elementName]}
        variant="outlined"
        width="100%"
        onChange={(value) => {
          dispatch({
            payload: { field: elementName, value },
            type: ACTION_TYPES.SET_FIELD,
          });
        }}
        {...props}
      />
    </WithLabel>
  );
};

const DisconnectedDatePicker = ({
  currentState,
  elementName,
  inputSx,
  label,
  wrapperSx,
  ...props
}: Omit<DateSelectorProps, "onChange"> & {
  currentState: TransitionalCareRequest;
  elementName: typeof REQUEST_FORM_FIELDS.REQUEST_SENT_DATE;
  wrapperSx?: SxProps;
}) => {
  const { dispatch, state } = useRequestListContext();

  const validation = state.validation[elementName];

  return (
    <WithLabel
      bold
      error={!isValid(validation)}
      label={label}
      required
      wrapperSx={{
        minWidth: SMALL_INPUT_MIN_WIDTH,
        width: SMALL_INPUT_WIDTH,
        ...(wrapperSx ?? {}),
      }}
    >
      <DatePickerInputField
        {...props}
        elementName={elementName}
        inputSx={{
          background: WHITE,
          width: "100%",
          ...(inputSx ?? {}),
        }}
        required
        textInputVariant="outlined"
        validation={validation}
        value={currentState[elementName] as number}
        onChange={(value) => {
          dispatch({
            payload: { field: elementName, value },
            type: ACTION_TYPES.SET_FIELD,
          });
        }}
      />
    </WithLabel>
  );
};

const DisconnectedTimePicker = ({
  currentState,
  elementName,
  label,
  wrapperSx,
  ...props
}: Omit<TimePresenterProps, "onChange" | "value" | "validation"> & {
  currentState: TransitionalCareRequest;
  elementName: typeof REQUEST_FORM_FIELDS.REQUEST_SENT_TIME;
  wrapperSx?: SxProps;
}) => {
  const { dispatch, state } = useRequestListContext();

  const validation = state.validation[elementName];

  return (
    <WithLabel
      bold
      error={!isValid(validation)}
      label={label}
      required
      wrapperSx={{
        minWidth: dp(110),
        width: SMALL_INPUT_WIDTH,
        ...(wrapperSx ?? {}),
      }}
    >
      <TimePickerPresenter
        elementName={elementName}
        required
        textInputVariant="outlined"
        validation={validation}
        value={fromUnixTime(currentState[elementName] as number)}
        width="100%"
        onChange={(value) => {
          dispatch({
            payload: { field: elementName, value: getUnixTime(value as Date) },
            type: ACTION_TYPES.SET_FIELD,
          });
        }}
        {...props}
      />
    </WithLabel>
  );
};

export const DisconnectedSelect = ({
  currentState,
  elementName,
  label,
  ...props
}: Omit<SelectInputProps, "value" | "onChange"> & {
  currentState: TransitionalCareRequest;
  elementName: typeof REQUEST_FORM_FIELDS.REQUEST_STATUS;
}) => {
  const { dispatch, state } = useRequestListContext();

  const validation = state.validation[elementName];

  return (
    <WithLabel
      bold
      error={!isValid(validation)}
      label={label}
      required
      wrapperSx={{
        width: SMALL_INPUT_WIDTH,
      }}
    >
      <SelectInput
        id={elementName}
        label={null}
        required
        {...props}
        inputSx={{
          background: WHITE,
          width: "100%",
        }}
        size="small"
        validation={validation}
        value={currentState[elementName] as number}
        variant="outlined"
        onChange={(value) => {
          dispatch({
            payload: { field: elementName, value: value as number },
            type: ACTION_TYPES.SET_FIELD,
          });
        }}
      />
    </WithLabel>
  );
};

export const EditCard = ({
  displayId,
  isEdit = false,
  request,
}: {
  displayId: number;
  isEdit?: boolean;
  request: TransitionalCareRequest | null;
}) => {
  const { dispatch } = useRequestListContext();
  const translations = useTranslations();
  const selectOptions = useOntologyOptions({
    type: ONTOLOGIES.TRANSITIONAL_CARE_REQUEST_STATUS,
  });

  const subFormRef = useRef<HTMLDivElement>(null);

  if (!request) return null;

  return (
    <VerticalLayout
      ref={subFormRef}
      backgroundColor={WHITE}
      boxSizing="border-box"
      data-testid={`edit-card-${displayId}`}
      margin={margin(2, 0, 1)}
      padding={padding(1, 2, 3)}
      style={{ flexDirection: "column-reverse" }} // to allow tab order to flow to the confirm buttons after the input elements
      width="100%"
    >
      <VerticalLayout>
        <SectionRow>
          <DisconnectedTextInput
            currentState={request}
            elementName="careprovider_name"
            label={
              translations.patientForms.transitionalCareForm.sectionE.editName
            }
            large
          />
          <DisconnectedTextInput
            currentState={request}
            elementName="careprovider_address_address"
            label={
              translations.patientForms.transitionalCareForm.sectionE.editStreet
            }
            large
          />
        </SectionRow>
        <SectionRow>
          <DisconnectedTextInput
            currentState={request}
            elementName="careprovider_address_zipcode"
            label={
              translations.patientForms.transitionalCareForm.sectionE
                .editZipCode
            }
          />
          <DisconnectedTextInput
            currentState={request}
            elementName="careprovider_address_city"
            label={
              translations.patientForms.transitionalCareForm.sectionE.editCity
            }
            large
          />
        </SectionRow>
        <SectionRow>
          <DisconnectedDatePicker
            currentState={request}
            elementName="request_sent_date"
            label={
              translations.patientForms.transitionalCareForm.sectionE.editDate
            }
          />
          <DisconnectedTimePicker
            currentState={request}
            elementName="request_sent_time"
            label={
              translations.patientForms.transitionalCareForm.sectionE.editTime
            }
          />
          <DisconnectedSelect
            currentState={request}
            elementName="request_status"
            label={
              translations.patientForms.transitionalCareForm.sectionE.editStatus
            }
            options={selectOptions}
          />
        </SectionRow>
      </VerticalLayout>
      <HorizontalLayout aligned justify="space-between" width="100%">
        <Body
          data-testid={`sectionid-${displayId}`}
          fontWeight={FONT_WEIGHT_SEMI_BOLD}
          margin="0"
        >
          {getSectionIdLabel(displayId)}
        </Body>
        <HorizontalLayout
          aligned
          justify="space-between"
          padding={padding(0, 0.5, 0, 0)}
        >
          <RSButton
            color="primary"
            id={isEdit ? "save_request" : "add_new_request"}
            loading="na"
            variant="contained"
            onClick={() => {
              handleInputFocusOnSubmit(
                (subFormRef.current?.getElementsByTagName("input") ||
                  []) as HTMLInputElement[],
              );
              dispatch({ type: ACTION_TYPES.SAVE });
            }}
          >
            {translations.patientForms.transitionalCareForm.sectionE.saveButton}
          </RSButton>
          <CardIconButton
            ariaLabel={
              translations.patientForms.transitionalCareForm.sectionE
                .deleteButtonAria
            }
            Icon={Trash2Icon}
            onClick={() =>
              dispatch({
                type: ACTION_TYPES.CLOSE_FORM,
              })
            }
          />
        </HorizontalLayout>
      </HorizontalLayout>
    </VerticalLayout>
  );
};
